// src/components/ui/ConfirmDialog.tsx
import React, { useState, useEffect } from "react";
import { TEXT_BUTTON_CLASS } from "../utils/buttonStyles";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  loading?: boolean;
  requireInput?: boolean; // shows password input when true
  inputLabel?: string;
  onConfirm: (inputValue?: string) => void;
  onCancel: () => void;
  /** Label for the confirm button (default "Confirm"). */
  confirmLabel?: string;
  /** Label for the cancel button (default "Cancel"). */
  cancelLabel?: string;
  /** Style the confirm button as a destructive action. */
  destructive?: boolean;
  /**
   * Optional third choice rendered between Cancel and Confirm, e.g. the
   * "No, keep them" answer of a yes/no question.
   */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  loading,
  requireInput = false,
  inputLabel = "Password",
  onConfirm,
  onCancel,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  secondaryLabel,
  onSecondary,
}: ConfirmDialogProps) {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (!open) setInputValue("");
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="bg-base-100 rounded-2xl shadow-xl w-full max-w-md p-6 sm:p-8">
        <h2
          id="confirm-dialog-title"
          className="text-xl font-grotesk mb-2 text-base-content"
        >
          {title}
        </h2>
        <p className="text-base-content/80 text-base sm:text-lg mb-4">
          {message}
        </p>

        {requireInput && (
          <input
            type="password"
            placeholder={inputLabel}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className="w-full bg-base-200 border border-base-300 rounded-md px-3 py-2 mb-4 focus:transparent text-base-content placeholder-base-content/60"
          />
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className={TEXT_BUTTON_CLASS}
          >
            {cancelLabel}
          </button>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            {secondaryLabel && onSecondary && (
              <button
                type="button"
                onClick={onSecondary}
                disabled={loading}
                className="px-4 py-2.5 rounded-xl bg-base-200 hover:bg-base-300 font-grotesk text-base-content disabled:opacity-50"
              >
                {secondaryLabel}
              </button>
            )}
            <button
              type="button"
              disabled={loading}
              onClick={() => onConfirm(inputValue)}
              className={`px-4 py-2.5 rounded-xl font-grotesk disabled:opacity-50 ${
                destructive
                  ? "bg-error text-error-content hover:bg-error/90"
                  : "bg-neutral text-neutral-content hover:bg-neutral"
              }`}
            >
              {loading ? "Processing..." : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
